import { Grid } from "@mui/material";
import { useContext, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../../../app/hooks";
import DialogComponent from "../../../../../../common/components/functional/dialogs/DialogComponent";
import FormTable from "../../../../../../common/components/functional/formTable/FormTable";
import InfoTooltip from "../../../../../../common/components/functional/InfoTooltip/InfoTooltip";
import { AccessRightContext } from "../../../../../user/AccessRightProvider";
import { StyledFormSection } from "../../../styles";
import WorkTitleSpecificPricingForm from "./WorkTitleSpecificPricingForm";

const MAIN_FORM_PROPERTY_KEY = "worktitles";
const DRAG_HANDLE_SELECTOR = "drag-handle-work-title-specific-pricing";

interface Props {
  disabled?: boolean;
  canEdit: boolean;
}

const WorkTitleSpecificPricingSection = ({ disabled, canEdit }: Props) => {
  const { t } = useTranslation("customers");
  const { setValue: setMainFormValue, watch: watchMainForm } = useFormContext();
  const { canEditContract } = useContext(AccessRightContext);
  const { workTitle } = useAppSelector((state) => state.localized);
  const [modalState, setModalState] = useState<{
    show: boolean;
    selectedItemId?: string;
  }>({
    show: false,
    selectedItemId: "",
  });

  const onOpenModal = (selectedItemId?: string) =>
    setModalState({ show: true, selectedItemId });
  const onCloseModal = () => setModalState({ show: false, selectedItemId: "" });

  const workTitlesArray = watchMainForm(MAIN_FORM_PROPERTY_KEY);

  const getWorkTitlesArray = () => {
    if (!workTitlesArray) return [];

    return workTitlesArray.map((workTitlePricingData: any) => ({
      id: workTitlePricingData.workTitleId,
      values: {
        name: workTitle[workTitlePricingData.workTitleId],
      },
      deletable: !disabled,
    }));
  };
  const workTitleSpecificPricingsData = getWorkTitlesArray();

  const headers = [{ text: t("common:workTitle") }];

  const addButtonLabel = disabled
    ? undefined
    : t("customers:contract.addWorkTitleSpecificPricing");

  const handleDelete = (id: string) => {
    // Remove id item from workTitlesArray
    const updatedWorkTitlesArray = workTitlesArray.filter(
      (item: any) => item.workTitleId !== id,
    );
    // Set updated array to main form
    setMainFormValue(MAIN_FORM_PROPERTY_KEY, updatedWorkTitlesArray, {
      shouldDirty: true,
    });
  };

  const Tooltip = (
    <Grid item marginLeft="18px" marginBottom="5px" zIndex={1}>
      <InfoTooltip title={t("contract.workTitleSpecificPricingTooltip")} />
    </Grid>
  );

  return (
    <StyledFormSection
      title={t("contract.workTitleSpecificPricing")}
      infoTooltip={Tooltip}
      noBorder
      disablePaddingBottom
    >
      <Grid container>
        <Grid item xs={3} />
        <Grid item>
          <FormTable
            disableEditRow={!canEditContract}
            data={workTitleSpecificPricingsData}
            headers={headers}
            contentKey=""
            handleOpenEditModal={(_, id) => {
              onOpenModal(id);
            }}
            addButtonLabel={addButtonLabel}
            handleDelete={(id) => handleDelete(id)}
          />
        </Grid>
      </Grid>
      <DialogComponent
        open={modalState.show}
        onClose={onCloseModal}
        sx={{ maxWidth: "931px" }}
        dragHandleSelector={DRAG_HANDLE_SELECTOR}
      >
        <WorkTitleSpecificPricingForm
          selectedItemId={modalState.selectedItemId}
          disabled={disabled}
          onClose={onCloseModal}
          canEdit={canEdit}
        />
      </DialogComponent>
    </StyledFormSection>
  );
};

export default WorkTitleSpecificPricingSection;
